import { omit } from 'ramda';
import { Box, InputLabel, Stack, Typography } from '@mui/material';
import HelpPopover from '../../../HelpPopover';
import { DeviceCardProps } from '../DeviceCardProps';
import DriveUnitPort from './DriveUnitPort';
import { useConfigurePanelPortChange } from '../../../../hooks/controls/useConfigurePanelPortChange';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../../../redux/ApplicationSlice';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN, isUnsupportedDriveUnitPortRawValue } from '../../../../utils/driveUnitPortSelection';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';

const DRIVE_UNIT_PORT_NUMBERS = Array.from({ length: DRIVE_UNIT_PORT_MAX - DRIVE_UNIT_PORT_MIN + 1 }, (_, index) => DRIVE_UNIT_PORT_MIN + index);
const DRIVE_UNIT_PORT_ROWS = [DRIVE_UNIT_PORT_NUMBERS.slice(0, 4), DRIVE_UNIT_PORT_NUMBERS.slice(4, 8)] as const;

export interface DriveUnitPortControlLabels {
  labelId: string;
  label: React.ReactNode;
  selectId: string;
  selectLabelId: string;
  labelPopover?: React.ReactNode;
  labelPopoverId?: string;
}

interface DriveUnitPortControlProps extends DeviceCardProps, DriveUnitPortControlLabels {
  /**
   * When `setPortControlValue` is set, port state and editor sync are owned by the parent (e.g. hoisted above Collapse).
   * Omit to use internal `useControlInitialization`.
   */
  portControlValue?: number;
  setPortControlValue?: (value: number) => void;
  /** When true, port changes update local/parent state only (no Monaco `updateParameter`). */
  skipEditorParameterSync?: boolean;
  portConflictPresentation?: 'dialog' | 'inline';
  unsupportedRawValue?: string;
}

type DriveUnitPortControlViewProps = DeviceCardProps &
  DriveUnitPortControlLabels & {
    port: number | undefined;
    onPortChange: (portNumber: number) => void;
    portConflictPresentation?: 'dialog' | 'inline';
    unsupportedRawValue?: string;
  };

/** Presentational port grid; no hooks (safe to render inside Collapse). */
const DriveUnitPortControlView = (props: DriveUnitPortControlViewProps) => {
  const { port, onPortChange, labelId, label, labelPopover, labelPopoverId } = props;
  const electronicKey = `${props.id}:${props.parameterName}`;
  const selectedSlot = typeof port === 'number' && Number.isFinite(port) ? port : Number.NaN;
  const conflictPresentation = props.portConflictPresentation ?? 'dialog';

  return (
    <Box>
      <InputLabel sx={{ paddingBottom: 1 }} id={labelId}>
        {labelPopover && labelPopoverId ? (
          <Stack direction="row" spacing={1}>
            <Typography>{label}</Typography>
            <HelpPopover id={labelPopoverId} text={labelPopover} />
          </Stack>
        ) : (
          label
        )}
      </InputLabel>
      <Stack direction="column" spacing={1}>
        <UnsupportedCodeStatus
          code={props.unsupportedRawValue}
          descriptionI18nKey="controlPanel.devices.unsupportedPortArgument.description"
          descriptionDefaultValue="Unsupported port argument: {{code}}"
        />
        {DRIVE_UNIT_PORT_ROWS.map((row, rowIndex) => (
          <Stack key={rowIndex} direction="row" spacing={1}>
            {row.map((portNumber) => (
              <DriveUnitPort
                key={portNumber}
                id={electronicKey}
                portNumber={portNumber}
                selected={selectedSlot}
                onClick={() => onPortChange(portNumber)}
                conflictPresentation={conflictPresentation}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

/** Port grid with internal `useControlInitialization` (legacy path for cards that mount this inside Collapse). */
const DriveUnitPortControlInternal = (props: DriveUnitPortControlProps) => {
  const {
    value: port,
    setValue: setPort,
    unsupportedRawValue,
  } = useControlInitialization<number>(props, {
    parseValue: (v) => Number.parseInt(v, 10),
    isUnsupportedRawValue: isUnsupportedDriveUnitPortRawValue,
  });
  const dispatch = useDispatch();
  const electronicKey = `${props.id}:${props.parameterName}`;
  const registryRev = props.driveUnitPortRegistryRev ?? 0;
  const effectivePort = unsupportedRawValue ? undefined : port;

  useEffect(() => {
    if (
      typeof effectivePort !== 'number' ||
      !Number.isFinite(effectivePort) ||
      effectivePort < DRIVE_UNIT_PORT_MIN ||
      effectivePort > DRIVE_UNIT_PORT_MAX
    ) {
      dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: electronicKey }));
      return;
    }
    dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: effectivePort, electronic: electronicKey }));
  }, [dispatch, effectivePort, electronicKey, registryRev]);

  const commitPortChange = useConfigurePanelPortChange(props, port, setPort);
  const cardFields = omit(['portControlValue', 'setPortControlValue', 'skipEditorParameterSync'], props) as Omit<
    DriveUnitPortControlViewProps,
    'port' | 'onPortChange'
  >;
  return (
    <DriveUnitPortControlView
      {...cardFields}
      port={effectivePort}
      onPortChange={commitPortChange}
      labelId={props.labelId}
      label={props.label}
      selectId={props.selectId}
      selectLabelId={props.selectLabelId}
      labelPopover={props.labelPopover}
      labelPopoverId={props.labelPopoverId}
      unsupportedRawValue={unsupportedRawValue}
    />
  );
};

const DriveUnitPortControlHoisted = (
  props: DriveUnitPortControlProps & {
    portControlValue: number | undefined;
    setPortControlValue: (value: number) => void;
  }
) => {
  const commitPortChange = useConfigurePanelPortChange(props, props.portControlValue, props.setPortControlValue);
  const cardFields = omit(['portControlValue', 'setPortControlValue', 'skipEditorParameterSync'], props) as Omit<
    DriveUnitPortControlViewProps,
    'port' | 'onPortChange'
  >;
  return (
    <DriveUnitPortControlView
      {...cardFields}
      port={props.portControlValue}
      onPortChange={commitPortChange}
      labelId={props.labelId}
      label={props.label}
      selectId={props.selectId}
      selectLabelId={props.selectLabelId}
      labelPopover={props.labelPopover}
      labelPopoverId={props.labelPopoverId}
      unsupportedRawValue={props.unsupportedRawValue}
    />
  );
};

const DriveUnitPortControl = (props: DriveUnitPortControlProps) => {
  if (props.setPortControlValue !== undefined) {
    return <DriveUnitPortControlHoisted {...props} portControlValue={props.portControlValue} setPortControlValue={props.setPortControlValue} />;
  }

  return <DriveUnitPortControlInternal {...props} />;
};

export default DriveUnitPortControl;
